import { Dispatch, SetStateAction } from 'react'

import { Box, Divider, Grid, ListItem, ListItemButton, List as MuiList, Stack, Typography } from '@mui/material'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation, usePageTitle } from 'hooks'
import IconArrow from 'pages/notices/assets/images/arrow.svg'

import {
  arrowIconStyle,
  buttonGridStyle,
  containerStyle,
  contentBoxStyle,
  contentGridExtendedStyle,
  dateTimeTextStyle,
  fullWidthStyle,
  listContainerStyle,
  listItemButtonStyle,
  listStyle,
  loadingContainerStyle,
  loadingItemStyle,
  newButtonContainerStyle,
  newButtonStyle,
  primaryTextStyle,
  titleBoxStyle,
  titleTextStyle,
  titleTypographyStyle
} from './styled'

type Props = {
  isLoading: boolean
  currentVersion: string
  noticeList: {
    id: string
    title: string
    uri: string
    description: string
    published: string
  }[]
  setNoticeLastItem: Dispatch<SetStateAction<HTMLElement | null>>
  handleClickItem: (string) => void
  showNewButton: boolean
  showOldButton: boolean
}

export default function List({
  isLoading,
  currentVersion,
  noticeList,
  setNoticeLastItem,
  handleClickItem,
  showNewButton,
  showOldButton
}: Props) {
  const t = useMappedTranslation()
  usePageTitle(t('notice.title'))

  const dateTime = useDateTime()

  return (
    <Box sx={containerStyle}>
      <Box sx={contentBoxStyle}>
        <Box sx={titleBoxStyle}>
          <Typography variant='h1' sx={titleTypographyStyle}>
            {t('notice.title')}
          </Typography>
        </Box>

        <Box sx={listContainerStyle}>
          {noticeList.length === 0 && isLoading ? (
            <Loading fullScreen />
          ) : (
            <MuiList sx={listStyle}>
              <Divider />
              {showNewButton && (
                <Box sx={newButtonContainerStyle}>
                  <ListItemButton
                    sx={newButtonStyle}
                    onClick={() => {
                      handleClickItem('new')
                    }}
                    data-testid='ListItemButton0D90DF71'
                  >
                    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={fullWidthStyle}>
                      <Grid container sx={buttonGridStyle}>
                        <Typography variant='subtitle1' color='text.primary' sx={primaryTextStyle}>
                          {t('notice.new.button')}
                        </Typography>
                      </Grid>
                      <Box component='img' src={IconArrow} alt='' />
                    </Stack>
                  </ListItemButton>
                  <Divider />
                </Box>
              )}
              {noticeList.map((notice, index) => {
                return (
                  <Box
                    key={index}
                    id={notice.id}
                    ref={currentVersion === 'NEW' && noticeList.length === index + 1 ? setNoticeLastItem : null}
                  >
                    <ListItemButton
                      sx={listItemButtonStyle}
                      onClick={() => {
                        handleClickItem(notice.id)
                      }}
                      data-testid='ListItemButtonA1A8DB62'
                    >
                      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={fullWidthStyle}>
                        <Grid container sx={contentGridExtendedStyle}>
                          <Typography variant='subtitle1' color='text.primary' sx={titleTextStyle}>
                            {notice.title}
                          </Typography>
                          <Typography variant='label1' className='off' sx={dateTimeTextStyle}>
                            {dateTime(Number(notice.published))}
                          </Typography>
                        </Grid>
                        <Box sx={arrowIconStyle}>
                          <Box component='img' src={IconArrow} alt='' />
                        </Box>
                      </Stack>
                    </ListItemButton>
                    {noticeList.length === index + 1 ? '' : <Divider />}
                  </Box>
                )
              })}
              {showOldButton && (
                <Box sx={newButtonContainerStyle}>
                  <Divider />
                  <ListItemButton
                    sx={listItemButtonStyle}
                    onClick={() => {
                      handleClickItem('old')
                    }}
                    data-testid='ListItemButton81DFC9C7'
                  >
                    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={fullWidthStyle}>
                      <Grid container sx={buttonGridStyle}>
                        <Typography variant='subtitle1' color='text.primary' sx={primaryTextStyle}>
                          {t('notice.legacy.button')}
                        </Typography>
                      </Grid>
                      <Box component='img' src={IconArrow} alt='' />
                    </Stack>
                  </ListItemButton>
                  <Divider />
                </Box>
              )}
              {isLoading && (
                <Box sx={loadingContainerStyle}>
                  <Divider />
                  <ListItem sx={loadingItemStyle}>
                    <Loading />
                  </ListItem>
                </Box>
              )}
            </MuiList>
          )}
        </Box>
      </Box>
    </Box>
  )
}
